import PageHeading from "../../../components/desktop/PageHeading";
import React, { useEffect, useRef, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import "./Weekoffroster.css";
import { api } from "../../../utils/api";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import UserSearchSelect from "../../../components/desktop/UserSearchSelect";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();

  return {
    from_date: formatInputDate(
      new Date(today.getFullYear(), today.getMonth(), 1),
    ),
    to_date: formatInputDate(
      new Date(today.getFullYear(), today.getMonth() + 1, 0),
    ),
  };
};

const getResponseList = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

const normalizeImportDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatInputDate(value);
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(
        parsed.d,
      ).padStart(2, "0")}`;
    }
  }

  const date = String(value || "").trim().split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;

  const dayFirstMatch = date.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dayFirstMatch) {
    return `${dayFirstMatch[3]}-${dayFirstMatch[2]}-${dayFirstMatch[1]}`;
  }

  return "";
};

const WEEK_DAYS = [
  { value: 1, label: "Sunday" },
  { value: 2, label: "Monday" },
  { value: 3, label: "Tuesday" },
  { value: 4, label: "Wednesday" },
  { value: 5, label: "Thursday" },
  { value: 6, label: "Friday" },
  { value: 7, label: "Saturday" },
];

const normalizeWeekoffDays = (value) => {
  const days = Array.isArray(value)
    ? value
    : String(value ?? "")
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean);

  return [
    ...new Set(
      days.map((day) => Number(String(day).split("|")[0].trim())),
    ),
  ].filter(
    (day) => Number.isInteger(day) && day >= 1 && day <= 7,
  );
};

const formatWeekoffDay = (dayId) => {
  const day = WEEK_DAYS.find((item) => item.value === Number(dayId));
  return day ? `${day.value} | ${day.label}` : String(dayId);
};

export default function Weekoffroster() {
  const { showLoader, hideLoader } = useLoaderStore();
  const { user } = useAuthStore();
  const companyId = user?.company_id;

  const [users, setUsers] = useState([]);
  const [weekoffLoading, setWeekoffLoading] = useState(false);
  const [weekoffAssignments, setWeekoffAssignments] = useState([]);

  const [weekoffFilter, setWeekoffFilter] = useState(getCurrentMonthRange);
  const [searchUser, setSearchUser] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  // User filter captured when Search is pressed, like the Report page
  const [appliedUserFilter, setAppliedUserFilter] = useState({
    ids: [],
    query: "",
  });

  const weekoffFromDateRef = useRef(null);
  const weekoffToDateRef = useRef(null);
  const weekoffAssignmentFromDateRefs = useRef({});
  const weekoffAssignmentToDateRefs = useRef({});
  const weekoffImportInputRef = useRef(null);
  const [isTemplateDownloading, setIsTemplateDownloading] = useState(false);
  const [showDownloadChoice, setShowDownloadChoice] = useState(false);
  const [isImportPreparing, setIsImportPreparing] = useState(false);
  const [isWeekoffImporting, setIsWeekoffImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const fetchUsers = async () => {
    if (!companyId) return;

    try {
      const response = await api.get(`/user/active-list/${companyId}`);
      const result = await response.json();
      const list = getResponseList(result);

      setUsers(list);
      return list;
    } catch (error) {
      console.error("FETCH USERS ERROR:", error);
      toast.error(error.message || "Failed to load usernames");
    }
  };

  const fetchWeekoffAssignments = async (
    fromDate = weekoffFilter.from_date,
    toDate = weekoffFilter.to_date,
  ) => {
    try {
      if (!companyId) return;

      if (!fromDate || !toDate) {
        const currentMonth = getCurrentMonthRange();
        fromDate = currentMonth.from_date;
        toDate = currentMonth.to_date;
      }

      if (new Date(fromDate) > new Date(toDate)) {
        toast.warning("From Date cannot be after To Date");
        return;
      }

      setWeekoffLoading(true);

      const response = await api.get(
        `/weekoff-assignment/list/${companyId}?from_date=${fromDate}&to_date=${toDate}`,
      );

      const result = await response.json();
      const assignments = getResponseList(result);

      const normalizedAssignments = assignments.map((assignment) => ({
        ...assignment,
        assignment_id: assignment.assignment_id ?? assignment.id ?? null,
        user_id: assignment.user_id ?? assignment.user?.id ?? "",
        username:
          assignment.username ??
          assignment.user_name ??
          assignment.employee_name ??
          assignment.user?.username ??
          [
            assignment.first_name ?? assignment.user?.first_name,
            assignment.last_name ?? assignment.user?.last_name,
          ]
            .filter(Boolean)
            .join(" "),
        weekoff_days: normalizeWeekoffDays(assignment.weekoff_days),
        from_date: assignment.from_date?.split("T")[0] ?? "",
        to_date: assignment.to_date?.split("T")[0] ?? "",
        isactive:
          assignment.isactive === true || Number(assignment.isactive) === 1
            ? 1
            : 0,
      }));

      setWeekoffAssignments(normalizedAssignments);
      return normalizedAssignments;
    } catch (error) {
      console.error("FETCH WEEKOFF ASSIGNMENTS ERROR:", error);

      setWeekoffAssignments([]);

      toast.error(error.message || "Failed to load weekoff assignments");
    } finally {
      setWeekoffLoading(false);
    }
  };

  const validateDateYear = (date, fieldName) => {
    if (!date) {
      toast.warning(`Please select ${fieldName}`);
      return false;
    }

    const year = date.split("-")[0];

    if (!/^\d{4}$/.test(year)) {
      toast.warning(`${fieldName} must have a 4-digit year`);
      return false;
    }

    return true;
  };

  const getUserName = (assignment) => {
    if (assignment.username) return assignment.username;

    const matchedUser = users.find(
      (employee) => Number(employee.id) === Number(assignment.user_id),
    );

    if (!matchedUser) return "-";

    const fullName = `${matchedUser.first_name || ""} ${
      matchedUser.last_name || ""
    }`.trim();

    return matchedUser.username || fullName || matchedUser.employee_code || "-";
  };

  const isUserFilterApplied =
    appliedUserFilter.ids.length > 0 || Boolean(appliedUserFilter.query);

  // Selected users win over typed text; the original index is kept so row
  // edits still update the right assignment.
  const filteredWeekoffAssignments = weekoffAssignments
    .map((assignment, index) => ({ assignment, index }))
    .filter(({ assignment }) => {
      if (appliedUserFilter.ids.length > 0) {
        return appliedUserFilter.ids.includes(Number(assignment.user_id));
      }

      if (!appliedUserFilter.query) return true;

      const matchedUser = users.find(
        (employee) => Number(employee.id) === Number(assignment.user_id),
      );

      return [
        getUserName(assignment),
        matchedUser?.first_name,
        matchedUser?.last_name,
        matchedUser?.employee_code,
        assignment.user_id,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(appliedUserFilter.query),
      );
    });

  const handleWeekoffFilter = async () => {
    if (!weekoffFilter.from_date) {
      toast.warning("Please select From Date");
      return;
    }

    if (!weekoffFilter.to_date) {
      toast.warning("Please select To Date");
      return;
    }

    if (new Date(weekoffFilter.from_date) > new Date(weekoffFilter.to_date)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    try {
      showLoader("master");
      const assignments = await fetchWeekoffAssignments(
        weekoffFilter.from_date,
        weekoffFilter.to_date,
      );

      if (assignments && selectedUsers.length > 0) {
        const assignedUserIds = new Set(
          assignments.map((assignment) => Number(assignment.user_id)),
        );
        const unassignedNames = selectedUsers
          .filter((u) => !assignedUserIds.has(Number(u.id)))
          .map((u) => u.name || u.employee_code || u.id);

        if (unassignedNames.length > 0) {
          toast.warning(
            `${unassignedNames.join(", ")} ${
              unassignedNames.length === 1 ? "is" : "are"
            } not assigned a week off for the selected dates`,
          );
        }
      }

      setAppliedUserFilter({
        ids: selectedUsers.map((u) => Number(u.id)),
        query:
          selectedUsers.length > 0 ? "" : searchUser.trim().toLowerCase(),
      });
    } finally {
      hideLoader();
    }
  };

  const downloadWeekoffTemplate = async (includeAssignmentData = false) => {
    if (!companyId) return;

    try {
      setIsTemplateDownloading(true);
      showLoader("master");

      const [assignmentResponse, userResponse] = await Promise.all([
        api.get(`/weekoff-assignment/list/${companyId}`),
        api.get(`/user/active-list/${companyId}`),
      ]);

      const [assignmentResult, userResult] = await Promise.all([
        assignmentResponse.json(),
        userResponse.json(),
      ]);

      const assignments = getResponseList(assignmentResult);
      const activeUsers = getResponseList(userResult);
      const workbook = new ExcelJS.Workbook();
      const assignmentSheet = workbook.addWorksheet("Weekoff Assignments");
      const userSheet = workbook.addWorksheet("Users");
      const daySheet = workbook.addWorksheet("Weekoff Days");

      assignmentSheet.addRow([
        "user_id",
        "weekoff_days",
        "from_date",
        "to_date",
      ]);
      if (includeAssignmentData) {
        assignments.forEach((assignment) => {
          const userId = assignment.user_id ?? assignment.user?.id ?? "";
          const matchedUser = activeUsers.find(
            (employee) =>
              Number(employee.id ?? employee.user_id) === Number(userId),
          );
          const userName = matchedUser
            ? matchedUser.username ||
              `${matchedUser.first_name || ""} ${
                matchedUser.last_name || ""
              }`.trim() ||
              matchedUser.employee_code ||
              ""
            : assignment.username || assignment.user_name || "";

          assignmentSheet.addRow([
            userName ? `${userId} | ${userName}` : userId,
            normalizeWeekoffDays(assignment.weekoff_days)
              .map(formatWeekoffDay)
              .join(","),
            assignment.from_date?.split("T")[0] ?? "",
            assignment.to_date?.split("T")[0] ?? "",
          ]);
        });
      }

      userSheet.addRow([
        "user_id",
        "name",
        "department",
        "designation",
        "Dropdown Value",
      ]);
      activeUsers.forEach((employee) => {
        const userId = employee.id ?? employee.user_id ?? "";
        const name =
          employee.username ||
          `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
          employee.employee_code ||
          "";

        userSheet.addRow([
          userId,
          name,
          employee.department_name ??
            employee.department?.department_name ??
            employee.department?.name ??
            employee.department ??
            "",
          employee.designation_name ??
            employee.designation?.designation_name ??
            employee.designation?.name ??
            employee.designation ??
            "",
          `${userId} | ${name}`,
        ]);
      });

      daySheet.addRow([
        "day_id",
        "day_name",
        "Dropdown Value",
        "Selection Options",
      ]);
      WEEK_DAYS.forEach((day) => {
        daySheet.addRow([day.value, day.label, formatWeekoffDay(day.value)]);
      });

      // Include every non-empty day combination so Excel users can select
      // multiple week-off days without a macro-enabled workbook.
      const weekoffSelectionOptions = Array.from(
        { length: 2 ** WEEK_DAYS.length - 1 },
        (_, index) => index + 1,
      )
        .map((mask) =>
          WEEK_DAYS.filter((_, index) => mask & (1 << index))
            .map((day) => formatWeekoffDay(day.value))
            .join(","),
        )
        .sort((first, second) => {
          const dayCountDifference =
            first.split(",").length - second.split(",").length;
          return dayCountDifference || first.localeCompare(second);
        });
      weekoffSelectionOptions.forEach((option, index) => {
        daySheet.getCell(index + 2, 4).value = option;
      });

      [assignmentSheet, userSheet, daySheet].forEach((sheet) => {
        sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
        sheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF3867D6" },
        };
        sheet.views = [{ state: "frozen", ySplit: 1 }];
        sheet.columns.forEach((column) => {
          column.width = 22;
        });
      });

      userSheet.getColumn(5).hidden = true;
      daySheet.getColumn(4).hidden = true;

      const userOptionEnd = Math.max(activeUsers.length + 1, 2);
      const weekoffOptionEnd = weekoffSelectionOptions.length + 1;
      workbook.definedNames.add(
        `Users!$E$2:$E$${userOptionEnd}`,
        "WeekoffRosterUserOptions",
      );
      workbook.definedNames.add(
        `'Weekoff Days'!$D$2:$D$${weekoffOptionEnd}`,
        "WeekoffRosterDayOptions",
      );

      const dropdownValidation = (rangeName) => ({
        type: "list",
        allowBlank: false,
        formulae: [rangeName],
        showErrorMessage: true,
        errorTitle: "Select a listed value",
        error: "Please choose a value from the drop-down list.",
      });

      assignmentSheet.dataValidations.add(
        "A2:A501",
        dropdownValidation("WeekoffRosterUserOptions"),
      );
      assignmentSheet.dataValidations.add(
        "B2:B501",
        dropdownValidation("WeekoffRosterDayOptions"),
      );

      const fileData = await workbook.xlsx.writeBuffer();
      const fileUrl = URL.createObjectURL(
        new Blob([fileData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = includeAssignmentData
        ? "Weekoff_Assignments_With_Data.xlsx"
        : "Weekoff_Assignment_Import_Template.xlsx";
      link.click();
      URL.revokeObjectURL(fileUrl);

      toast.success(
        includeAssignmentData
          ? "Weekoff assignments downloaded with data"
          : "Blank weekoff assignment template downloaded",
      );
    } catch (error) {
      console.error("DOWNLOAD WEEKOFF TEMPLATE ERROR:", error);
      toast.error(error.message || "Unable to download weekoff template");
    } finally {
      hideLoader();
      setIsTemplateDownloading(false);
      setShowDownloadChoice(false);
    }
  };

  const handleImportWeekoffs = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !companyId) return;

    try {
      setIsImportPreparing(true);
      showLoader("master");

      const userResponse = await api.get(`/user/active-list/${companyId}`);
      const userResult = await userResponse.json();
      const activeUsers = getResponseList(userResult);

      const fileData = await file.arrayBuffer();
      const workbook = XLSX.read(fileData, {
        type: "array",
        cellDates: true,
      });
      const assignmentSheet =
        workbook.Sheets["Weekoff Assignments"] ||
        workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(assignmentSheet, { defval: "" });

      if (!rows.length) {
        throw new Error("The Weekoff Assignments sheet has no rows to import");
      }

      const invalidRows = [];
      const validRows = [];

      rows.forEach((row, index) => {
        const rowNumber = index + 2;
        const userId = Number(
          String(row.user_id || "")
            .split("|")[0]
            .trim(),
        );
        const weekoffDays = normalizeWeekoffDays(
          row.weekoff_days ?? row.weekoffdays,
        );
        const fromDate = normalizeImportDate(row.from_date ?? row.from);
        const toDate = normalizeImportDate(row.to_date ?? row.to);
        const matchedUser = activeUsers.find(
          (employee) =>
            Number(employee.id ?? employee.user_id) === Number(userId),
        );
        const reasons = [];

        if (!Number.isInteger(userId) || userId <= 0 || !matchedUser) {
          reasons.push("Select a valid active user");
        }
        if (!weekoffDays.length) reasons.push("Select at least one weekoff day");
        if (!fromDate) reasons.push("From Date is invalid");
        if (!toDate) reasons.push("To Date is invalid");
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
          reasons.push("From Date cannot be after To Date");
        }

        if (reasons.length) {
          invalidRows.push({ rowNumber, reason: reasons.join("; ") });
          return;
        }

        const userName =
          matchedUser.username ||
          `${matchedUser.first_name || ""} ${
            matchedUser.last_name || ""
          }`.trim() ||
          matchedUser.employee_code ||
          "-";
        const payload = {
          company_id: Number(companyId),
          user_id: userId,
          weekoff_days: weekoffDays,
          from_date: fromDate,
          to_date: toDate,
        };

        validRows.push({
          rowNumber,
          payload,
          userLabel: `${userId} | ${userName}`,
          weekoffLabel: weekoffDays
            .map(
              (dayId) =>
                WEEK_DAYS.find((day) => day.value === dayId)?.label || dayId,
            )
            .join(","),
        });
      });

      setImportPreview({ validRows, invalidRows });
      if (!validRows.length) {
        toast.error("No valid weekoff assignments were found in the file");
      }
    } catch (error) {
      console.error("PREPARE WEEKOFF IMPORT ERROR:", error);
      toast.error(error.message || "Unable to prepare weekoff import");
    } finally {
      hideLoader();
      setIsImportPreparing(false);
    }
  };

  const confirmWeekoffImport = async () => {
    if (!importPreview?.validRows.length) return;

    const payload = importPreview.validRows.map((row) => row.payload);

    try {
      setIsWeekoffImporting(true);
      setImportProgress({ current: 0, total: payload.length });
      showLoader("master");

      const response = await api.post(`/weekoff-assignment/bulk-add`, payload);
      const result = await response.json();

      if (result?.status === false) {
        throw new Error(result.message || "Weekoff import was rejected");
      }

      setImportProgress({ current: payload.length, total: payload.length });
      toast.success(
        `${payload.length} weekoff assignment${
          payload.length === 1 ? "" : "s"
        } imported successfully`,
      );
      await fetchWeekoffAssignments();
      setImportPreview(null);
    } catch (error) {
      console.error("IMPORT WEEKOFF ASSIGNMENTS ERROR:", error);
      toast.error(error.message || "Unable to import weekoff assignments");
    } finally {
      hideLoader();
      setIsWeekoffImporting(false);
    }
  };

  const handleWeekoffRowSave = async (assignment) => {
    // Validation
    if (!assignment.user_id) {
      toast.warning("Please select username");
      return;
    }

    const weekoffDays = normalizeWeekoffDays(assignment.weekoff_days);
    if (!weekoffDays.length) {
      toast.warning("Please select at least one weekoff day");
      return;
    }

    if (!validateDateYear(assignment.from_date, "From Date")) {
      return;
    }

    if (!validateDateYear(assignment.to_date, "To Date")) {
      return;
    }

    if (new Date(assignment.from_date) > new Date(assignment.to_date)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    try {
      showLoader("master");

      const payload = {
        company_id: Number(companyId),
        user_id: Number(assignment.user_id),
        weekoff_days: weekoffDays,
        from_date: assignment.from_date,
        to_date: assignment.to_date,
        isactive: Number(assignment.isactive) === 1,
      };

      const response = await api.put(
        `/weekoff-assignment/update/${assignment.assignment_id}`,
        payload,
      );

      const result = await response.json();

      if (result.status) {
        toast.success(
          "Weekoff assignment updated successfully",
        );

        // Reload assignments
        await fetchWeekoffAssignments();
      } else {
        toast.error(result.message || "Failed to save weekoff assignment");
      }
    } catch (error) {
      console.error("WEEKOFF ASSIGNMENT SAVE ERROR:", error);

      toast.error(error.message || "Failed to save weekoff assignment");
    } finally {
      hideLoader();
    }
  };

  const handleWeekoffStatusToggle = async (assignment, checked) => {
    try {
      showLoader("master");

      const payload = {
        company_id: Number(companyId),
        user_id: Number(assignment.user_id),
        weekoff_days: normalizeWeekoffDays(assignment.weekoff_days),
        from_date: assignment.from_date,
        to_date: assignment.to_date,
        isactive: checked,
      };

      const response = await api.put(
        `/weekoff-assignment/update/${assignment.assignment_id}`,
        payload,
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Failed to update weekoff status");
      }

      // Update UI immediately
      setWeekoffAssignments((prev) =>
        prev.map((item) =>
          item.assignment_id === assignment.assignment_id
            ? {
                ...item,
                isactive: checked ? 1 : 0,
              }
            : item,
        ),
      );

      toast.success(
        checked
          ? "Weekoff assignment activated successfully"
          : "Weekoff assignment deactivated successfully",
      );
    } catch (error) {
      console.error("WEEKOFF STATUS UPDATE ERROR:", error);

      // Keep the toggle unchanged if API fails
      setWeekoffAssignments((prev) =>
        prev.map((item) =>
          item.assignment_id === assignment.assignment_id
            ? {
                ...item,
                isactive: checked ? 0 : 1,
              }
            : item,
        ),
      );

      toast.error(error.message || "Failed to update weekoff status");
    } finally {
      hideLoader();
    }
  };

  const toggleWeekoffDay = (index, dayValue) => {
    setWeekoffAssignments((previous) =>
      previous.map((assignment, assignmentIndex) => {
        if (assignmentIndex !== index) return assignment;

        const selectedDays = normalizeWeekoffDays(assignment.weekoff_days);
        return {
          ...assignment,
          weekoff_days: selectedDays.includes(dayValue)
            ? selectedDays.filter((day) => day !== dayValue)
            : [...selectedDays, dayValue].sort((a, b) => a - b),
        };
      }),
    );
  };

  useEffect(() => {
    if (companyId) {
      const loadWeekoffRoster = async () => {
        try {
          showLoader("master");
          await Promise.all([fetchUsers(), fetchWeekoffAssignments()]);
        } finally {
          hideLoader();
        }
      };

      loadWeekoffRoster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="weekoff-roster-page-container">
      <PageHeading>Week Off Roster</PageHeading>
      {showDownloadChoice && (
        <div className="weekoff-roster-page-download-backdrop" role="dialog" aria-modal="true">
          <div className="weekoff-roster-page-download-modal">
            <h3>Download Weekoff Assignment Template</h3>
            <p>
              Choose whether to download a blank template or include existing
              weekoff assignments.
            </p>

            <div className="weekoff-roster-page-download-actions">
              <button
                type="button"
                className="weekoff-roster-page-action-btn"
                disabled={isTemplateDownloading}
                onClick={() => downloadWeekoffTemplate(false)}
              >
                {isTemplateDownloading ? "Preparing..." : "Without Data"}
              </button>

              <button
                type="button"
                className="weekoff-roster-page-action-btn weekoff-roster-page-import-btn"
                disabled={isTemplateDownloading}
                onClick={() => downloadWeekoffTemplate(true)}
              >
                {isTemplateDownloading ? "Preparing..." : "With Data"}
              </button>
            </div>

            <button
              type="button"
              className="weekoff-roster-page-download-cancel"
              disabled={isTemplateDownloading}
              onClick={() => setShowDownloadChoice(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {importPreview ? (
        <div className="weekoff-roster-page-preview-container">
          <div className="weekoff-roster-page-preview-header">
            <div>
              <h3>Review Weekoff Assignment Import</h3>
              <p>
                No assignments have been created yet. Confirm to import the
                valid rows below.
              </p>
            </div>

            <div className="weekoff-roster-page-preview-summary">
              <span className="weekoff-roster-page-valid-count">
                {importPreview.validRows.length} ready
              </span>
              <span className="weekoff-roster-page-invalid-count">
                {importPreview.invalidRows.length} invalid
              </span>
            </div>
          </div>

          {isWeekoffImporting && (
            <div className="weekoff-roster-page-progress" aria-live="polite">
              <div className="weekoff-roster-page-progress-label">
                <span>
                  Importing assignments: {importProgress.current} of{" "}
                  {importProgress.total}
                </span>
                <strong>
                  {importProgress.total
                    ? Math.round(
                        (importProgress.current / importProgress.total) * 100,
                      )
                    : 0}
                  %
                </strong>
              </div>

              <div className="weekoff-roster-page-progress-track">
                <div
                  className="weekoff-roster-page-progress-fill"
                  style={{
                    width: `${
                      importProgress.total
                        ? (importProgress.current / importProgress.total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="weekoff-roster-page-preview-table-wrapper">
            <table className="weekoff-roster-page-preview-table">
              <thead>
                <tr>
                  <th>Excel Row</th>
                  <th>User</th>
                  <th>Weekoff Days</th>
                  <th>From Date</th>
                  <th>To Date</th>
                </tr>
              </thead>

              <tbody>
                {importPreview.validRows.map(
                  ({ rowNumber, payload, userLabel, weekoffLabel }) => (
                    <tr key={rowNumber}>
                      <td>{rowNumber}</td>
                      <td>{userLabel}</td>
                      <td>{weekoffLabel}</td>
                      <td>{payload.from_date}</td>
                      <td>{payload.to_date}</td>
                    </tr>
                  ),
                )}

                {importPreview.invalidRows.map(({ rowNumber, reason }) => (
                  <tr
                    key={`invalid-${rowNumber}`}
                    className="weekoff-roster-page-invalid-row"
                  >
                    <td>{rowNumber}</td>
                    <td colSpan="4">Not ready: {reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="weekoff-roster-page-preview-actions">
            <button
              type="button"
              className="weekoff-roster-page-import-cancel-btn"
              disabled={isWeekoffImporting}
              onClick={() => setImportPreview(null)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="weekoff-roster-page-import-confirm-btn"
              disabled={!importPreview.validRows.length || isWeekoffImporting}
              onClick={confirmWeekoffImport}
            >
              {isWeekoffImporting ? "Importing Weekoffs..." : "Confirm Import"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ===== WEEK OFF ROSTER ASSIGNMENTS ===== */}
          <div className="weekoff-roster-page-assignment-section">
        <div className="weekoff-roster-page-table-header">
            {/* LEFT SIDE - DATE FILTER */}
            <div className="weekoff-roster-page-filter">
              <div className="weekoff-roster-page-filter-field">
                <label>From Date</label>

                <div className="weekoff-roster-page-searchbar-date-wrapper">
                  <input
                    type="text"
                    className="weekoff-roster-page-searchbar-date-display"
                    value={
                      weekoffFilter.from_date
                        ? weekoffFilter.from_date.split("-").reverse().join("-")
                        : ""
                    }
                    placeholder="dd-mm-yyyy"
                    readOnly
                    onClick={() => {
                      weekoffFromDateRef.current?.showPicker();
                    }}
                  />

                  <FiCalendar
                    className="weekoff-roster-page-searchbar-calendar-icon"
                    onClick={() => {
                      weekoffFromDateRef.current?.showPicker();
                    }}
                  />

                  <input
                    ref={weekoffFromDateRef}
                    type="date"
                    className="weekoff-roster-page-searchbar-date-picker"
                    min="2000-01-01"
                    max="2099-12-31"
                    value={weekoffFilter.from_date}
                    onChange={(e) =>
                      setWeekoffFilter((prev) => ({
                        ...prev,
                        from_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="weekoff-roster-page-filter-field">
                <label>To Date</label>

                <div className="weekoff-roster-page-searchbar-date-wrapper">
                  <input
                    type="text"
                    className="weekoff-roster-page-searchbar-date-display"
                    value={
                      weekoffFilter.to_date
                        ? weekoffFilter.to_date.split("-").reverse().join("-")
                        : ""
                    }
                    placeholder="dd-mm-yyyy"
                    readOnly
                    onClick={() => {
                      weekoffToDateRef.current?.showPicker();
                    }}
                  />

                  <FiCalendar
                    className="weekoff-roster-page-searchbar-calendar-icon"
                    onClick={() => {
                      weekoffToDateRef.current?.showPicker();
                    }}
                  />

                  <input
                    ref={weekoffToDateRef}
                    type="date"
                    className="weekoff-roster-page-searchbar-date-picker"
                    min="2000-01-01"
                    max="2099-12-31"
                    value={weekoffFilter.to_date}
                    onChange={(e) =>
                      setWeekoffFilter((prev) => ({
                        ...prev,
                        to_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <UserSearchSelect
                users={users}
                searchText={searchUser}
                onSearchTextChange={setSearchUser}
                selectedUsers={selectedUsers}
                onSelectedUsersChange={setSelectedUsers}
                onEnter={handleWeekoffFilter}
              />

              <button
                type="button"
                className="weekoff-roster-page-filter-btn"
                onClick={handleWeekoffFilter}
              >
                Search
              </button>
            </div>

            <div className="weekoff-roster-page-filter-actions">
              <button
                type="button"
                className="weekoff-roster-page-action-btn"
                disabled={isTemplateDownloading}
                onClick={() => setShowDownloadChoice(true)}
              >
                Download Template
              </button>

              <button
                type="button"
                className="weekoff-roster-page-action-btn weekoff-roster-page-import-btn"
                disabled={isImportPreparing}
                onClick={() => weekoffImportInputRef.current?.click()}
              >
                {isImportPreparing ? "Preparing Import..." : "Import Weekoff"}
              </button>

              <input
                ref={weekoffImportInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="weekoff-roster-page-import-file-input"
                onChange={handleImportWeekoffs}
              />
            </div>
          </div>

          <div className="weekoff-roster-page-table-wrapper">
            <table className="weekoff-roster-page-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Username</th>
                  <th>Weekoff Days</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th className="weekoff-roster-page-status-col">Status</th>
                  <th className="weekoff-roster-page-action-col">Action</th>
                </tr>
              </thead>

              <tbody>
                {weekoffLoading ? (
                  <tr>
                    <td colSpan="7" className="weekoff-roster-page-no-data">
                      Loading...
                    </td>
                  </tr>
                ) : filteredWeekoffAssignments.length > 0 ? (
                  filteredWeekoffAssignments.map(({ assignment, index }, rowIndex) => (
                    <tr key={assignment.assignment_id || `new-${index}`}>
                      {/* Sr. No. */}
                      <td className="weekoff-roster-page-serial-col">{rowIndex + 1}</td>

                      {/* Username */}
                      <td className="weekoff-roster-page-username-col">
                        {getUserName(assignment)}
                      </td>

                      {/* Weekoff Days */}
                      <td className="weekoff-roster-page-days-cell">
                        <div className="weekoff-roster-page-day-options">
                          {WEEK_DAYS.map((day) => (
                            <label
                              key={day.value}
                              className="weekoff-roster-page-day-option"
                            >
                              <input
                                type="checkbox"
                                checked={normalizeWeekoffDays(
                                  assignment.weekoff_days,
                                ).includes(day.value)}
                                onChange={() =>
                                  toggleWeekoffDay(index, day.value)
                                }
                              />
                              <span>{day.label}</span>
                            </label>
                          ))}
                        </div>
                      </td>

                      {/* From Date */}
                      <td>
                        <div className="weekoff-roster-page-assignment-date-wrapper">
                          <input
                            type="text"
                            className="weekoff-roster-page-assignment-date-display"
                            value={
                              assignment.from_date
                                ? assignment.from_date
                                    .split("-")
                                    .reverse()
                                    .join("-")
                                : ""
                            }
                            placeholder="dd-mm-yyyy"
                            readOnly
                            onClick={() => {
                              weekoffAssignmentFromDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <FiCalendar
                            className="weekoff-roster-page-assignment-calendar-icon"
                            onClick={() => {
                              weekoffAssignmentFromDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <input
                            ref={(el) => {
                              weekoffAssignmentFromDateRefs.current[index] = el;
                            }}
                            type="date"
                            className="weekoff-roster-page-assignment-date-picker"
                            min="2000-01-01"
                            max="2099-12-31"
                            value={assignment.from_date || ""}
                            onChange={(e) => {
                              const updated = [...weekoffAssignments];

                              updated[index] = {
                                ...updated[index],
                                from_date: e.target.value,
                              };

                              setWeekoffAssignments(updated);
                            }}
                          />
                        </div>
                      </td>

                      {/* To Date */}
                      <td>
                        <div className="weekoff-roster-page-assignment-date-wrapper">
                          <input
                            type="text"
                            className="weekoff-roster-page-assignment-date-display"
                            value={
                              assignment.to_date
                                ? assignment.to_date
                                    .split("-")
                                    .reverse()
                                    .join("-")
                                : ""
                            }
                            placeholder="dd-mm-yyyy"
                            readOnly
                            onClick={() => {
                              weekoffAssignmentToDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <FiCalendar
                            className="weekoff-roster-page-assignment-calendar-icon"
                            onClick={() => {
                              weekoffAssignmentToDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <input
                            ref={(el) => {
                              weekoffAssignmentToDateRefs.current[index] = el;
                            }}
                            type="date"
                            className="weekoff-roster-page-assignment-date-picker"
                            min="2000-01-01"
                            max="2099-12-31"
                            value={assignment.to_date || ""}
                            onChange={(e) => {
                              const updated = [...weekoffAssignments];

                              updated[index] = {
                                ...updated[index],
                                to_date: e.target.value,
                              };

                              setWeekoffAssignments(updated);
                            }}
                          />
                        </div>
                      </td>

                      {/* Status */}
                      <td className="weekoff-roster-page-status-col">
                        <label className="weekoff-roster-page-toggle">
                          <input
                            type="checkbox"
                            checked={Number(assignment.isactive) === 1}
                            onChange={(e) =>
                              handleWeekoffStatusToggle(
                                assignment,
                                e.target.checked,
                              )
                            }
                          />

                          <span className="weekoff-roster-page-toggle-slider"></span>
                        </label>
                      </td>

                      {/* Action */}
                      <td className="weekoff-roster-page-action-col">
                        <button
                          type="button"
                          className="weekoff-roster-page-save-btn"
                          onClick={() => handleWeekoffRowSave(assignment)}
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="weekoff-roster-page-no-data">
                      {isUserFilterApplied
                        ? "No matching users found."
                        : "No weekoff assignments found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
